export type BucketType = 'current-state' | 'problem-opportunity' | 'ramifications';

export interface ClassificationResult {
  bucket: BucketType | null;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  isCorrect: boolean | null; // null if not yet classified by user
  feedback?: string;
}

/**
 * Classify a sentence as Current State, Problem/Opportunity, or Ramifications
 * Uses rule-based logic to determine the most likely bucket
 */
export function classifySentence(sentence: string): ClassificationResult {
  const trimmed = sentence.trim();
  if (!trimmed) {
    return {
      bucket: null,
      confidence: 'low',
      reasoning: 'Empty sentence',
      isCorrect: null
    };
  }

  // Test for Current State
  const currentStateScore = testCurrentState(trimmed);
  // Test for Problem/Opportunity
  const problemScore = testProblemOpportunity(trimmed);
  // Test for Ramifications
  const ramificationsScore = testRamifications(trimmed);

  // Determine highest score
  const scores = [
    { bucket: 'current-state' as BucketType, score: currentStateScore },
    { bucket: 'problem-opportunity' as BucketType, score: problemScore },
    { bucket: 'ramifications' as BucketType, score: ramificationsScore }
  ];

  scores.sort((a, b) => b.score - a.score);
  const winner = scores[0];
  const runnerUp = scores[1];

  // Determine confidence
  let confidence: 'high' | 'medium' | 'low' = 'low';
  if (winner.score >= 3 && winner.score > runnerUp.score + 1) {
    confidence = 'high';
  } else if (winner.score >= 2) {
    confidence = 'medium';
  }

  return {
    bucket: winner.bucket,
    confidence,
    reasoning: getReasoning(winner.bucket, winner.score),
    isCorrect: null
  };
}

/**
 * Test if sentence belongs in Current State bucket
 * Returns score 0-5 based on how well it matches Current State criteria
 */
function testCurrentState(sentence: string): number {
  let score = 0;
  const lower = sentence.toLowerCase();

  // Positive indicators (facts, observations)
  if (/^(we|i|our|my|the|they|it|this|that)\s+(have|has|are|is|do|does|can|will|currently|now)/i.test(sentence)) {
    score += 2;
  }

  // Observable facts patterns
  if (/observable|fact|currently|right now|today|at this moment/i.test(lower)) {
    score += 1;
  }

  // Camera test - describes something that could be recorded
  if (/^(we|i|our|the|they)\s+(have|own|use|operate|run|manage|employ|serve|deliver|produce)/i.test(sentence)) {
    score += 1;
  }

  // Negative indicators (interpretations, judgments, consequences)
  if (/problem|issue|tension|friction|mismatch|gap|consequence|stake|risk|cost|impact|matters|because|if.*continues|will.*happen/i.test(lower)) {
    score -= 2;
  }

  // Action-oriented language (solutions/plans)
  if (/need to|should|must|will|going to|plan to|strategy|solution/i.test(lower)) {
    score -= 1;
  }

  // Value judgments
  if (/bad|good|insufficient|adequate|poor|excellent|better|worse|important|critical|urgent/i.test(lower)) {
    score -= 1;
  }

  return Math.max(0, score);
}

/**
 * Test if sentence belongs in Problem/Opportunity bucket
 * Returns score 0-5 based on how well it matches Problem criteria
 */
function testProblemOpportunity(sentence: string): number {
  let score = 0;
  const lower = sentence.toLowerCase();

  // Core issue indicators
  if (/problem|issue|tension|friction|mismatch|gap|challenge|difficulty|obstacle|barrier/i.test(lower)) {
    score += 2;
  }

  // Meaning/interpretation indicators
  if (/means|indicates|suggests|shows|reveals|demonstrates|reflects|represents/i.test(lower)) {
    score += 1;
  }

  // Core issue language
  if (/core|essence|underlying|fundamental|root|real|actual|true|nature|character/i.test(lower)) {
    score += 1;
  }

  // Functional gap language
  if (/limits|restricts|prevents|blocks|hinders|constrains|reduces|diminishes/i.test(lower)) {
    score += 1;
  }

  // "So what?" test - explains why facts matter
  if (/because|due to|as a result|leads to|creates|causes|produces/i.test(lower)) {
    score += 1;
  }

  // Negative indicators (pure facts or pure consequences)
  if (/^(we|i|our|the|they)\s+(have|has|are|is)\s+(a|an|the)\s+\w+\s+(that|which)/i.test(sentence) && 
      !/problem|issue|tension|gap/i.test(lower)) {
    score -= 1;
  }

  // Consequence language (belongs in Ramifications)
  if (/if.*continues|will.*happen|would.*lead|consequence|stake|risk|cost|impact|matters because|why.*matters/i.test(lower)) {
    score -= 1;
  }

  // Pure fact patterns
  if (/^(we|i|our|the|they)\s+(have|has|are|is)\s+\d+/i.test(sentence)) {
    score -= 1;
  }

  return Math.max(0, score);
}

/**
 * Test if sentence belongs in Ramifications bucket
 * Returns score 0-5 based on how well it matches Ramifications criteria
 */
function testRamifications(sentence: string): number {
  let score = 0;
  const lower = sentence.toLowerCase();

  // Consequence indicators
  if (/if.*continues|if.*nothing|if.*don't|if.*doesn't|will.*happen|would.*lead|will.*result/i.test(lower)) {
    score += 3;
  }

  // Stakes and urgency
  if (/stake|risk|cost|impact|consequence|result|outcome|effect|ramification/i.test(lower)) {
    score += 2;
  }

  // Value judgment language
  if (/matters|important|critical|urgent|significant|essential|vital|crucial/i.test(lower)) {
    score += 1;
  }

  // "Why now?" indicators
  if (/why.*matters|why.*important|why.*now|matters because|important because/i.test(lower)) {
    score += 2;
  }

  // Future consequences
  if (/will|would|could|might|may|future|eventually|over time|in time/i.test(lower) && 
      /lose|fail|miss|damage|harm|collapse|stall|decline|suffer/i.test(lower)) {
    score += 2;
  }

  // Negative indicators (facts or problems)
  if (/^(we|i|our|the|they)\s+(have|has|are|is)/i.test(sentence) && 
      !/will|would|if|consequence|stake/i.test(lower)) {
    score -= 1;
  }

  // Problem language without consequence
  if (/problem|issue|tension|gap|mismatch/i.test(lower) && 
      !/if|will|would|consequence|stake|matters/i.test(lower)) {
    score -= 1;
  }

  return Math.max(0, score);
}

/**
 * Get reasoning explanation for classification result
 */
function getReasoning(bucket: BucketType, score: number): string {
  if (score === 0) {
    return 'This sentence does not clearly match any bucket. Consider rewriting for clarity.';
  }

  switch (bucket) {
    case 'current-state':
      return 'This appears to be an observable fact or condition - something verifiable without interpretation.';
    case 'problem-opportunity':
      return 'This sentence interprets the situation and points toward the underlying issue.';
    case 'ramifications':
      return 'This describes a consequence or stake - what happens if nothing changes.';
  }
}

/**
 * Validate user's classification choice
 * Returns feedback on whether the sentence was correctly placed
 */
export function validateClassification(
  sentence: string,
  userBucket: BucketType
): ClassificationResult {
  const classification = classifySentence(sentence);
  const isCorrect = classification.bucket === userBucket;

  let feedback = '';
  if (isCorrect) {
    feedback = getCorrectFeedback(userBucket);
  } else {
    feedback = getIncorrectFeedback(userBucket, classification.bucket);
  }

  return {
    ...classification,
    isCorrect,
    feedback
  };
}

function getCorrectFeedback(bucket: BucketType): string {
  switch (bucket) {
    case 'current-state':
      return '✔ Mostly factual. This is an observation about how things are today. If any judgment is embedded, you can split the sentence: keep the facts here and move the interpretation to the Problem section.';
    case 'problem-opportunity':
      return '🎯 Good fit. This interprets the facts and identifies the core issue created by the current situation.';
    case 'ramifications':
      return '✔ Strong Ramification. You\'re describing what happens if nothing changes - the stakes, the cost, or the risk.';
  }
}

function getIncorrectFeedback(
  userBucket: BucketType,
  suggestedBucket: BucketType | null
): string {
  if (!suggestedBucket) {
    return 'This sentence is unclear. Consider rewriting it to be more specific.';
  }

  // User placed in Current State but it's actually Problem
  if (userBucket === 'current-state' && suggestedBucket === 'problem-opportunity') {
    return '⚠ Part fact, part interpretation. The observable part belongs here. The meaning or evaluation belongs in the Problem section.';
  }

  // User placed in Current State but it's actually Ramifications
  if (userBucket === 'current-state' && suggestedBucket === 'ramifications') {
    return '✖ Not a Current State sentence. This describes either the meaning of the facts or the consequences - both of which belong elsewhere.';
  }

  // User placed in Problem but it's actually Current State
  if (userBucket === 'problem-opportunity' && suggestedBucket === 'current-state') {
    return '✖ Not a Problem sentence. This is either a fact (Current State) or a consequence (Ramifications). Try sorting it again.';
  }

  // User placed in Problem but it's actually Ramifications
  if (userBucket === 'problem-opportunity' && suggestedBucket === 'ramifications') {
    return '⚠ Contains both Problem and Ramifications. If the sentence says why this matters, move the "why" to Ramifications and keep the core issue here.';
  }

  // User placed in Ramifications but it's actually Current State
  if (userBucket === 'ramifications' && suggestedBucket === 'current-state') {
    return '✖ This isn\'t a future consequence. Ramifications should answer: "Why does this matter enough to fix?"';
  }

  // User placed in Ramifications but it's actually Problem
  if (userBucket === 'ramifications' && suggestedBucket === 'problem-opportunity') {
    return '⚠ This is tension, not consequence. You\'re describing the nature of the issue, not the impact of staying on the current path. Move this to the Problem section.';
  }

  return 'Consider reviewing the classification rules for this bucket.';
}

/**
 * Split paragraph into sentences for multi-sentence classification
 */
export function splitIntoSentences(text: string): string[] {
  // Split on sentence endings, but preserve the punctuation
  const sentences = text
    .split(/([.!?]+\s+)/)
    .filter(s => s.trim().length > 0)
    .reduce((acc: string[], curr, idx, arr) => {
      if (idx % 2 === 0) {
        // Even index is sentence text
        const next = arr[idx + 1] || '';
        acc.push((curr + next).trim());
      }
      return acc;
    }, [])
    .filter(s => s.length > 0);

  return sentences.length > 0 ? sentences : [text];
}

